import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { ArrowLeft, Edit } from 'lucide-react';
import { findScoringRuns, getServeRotationStats, isAce, isTeamAPoint, isTeamBPoint, scoringBoxClassName, scoringBoxLabel } from '../utils/scoringOrder';
import { elapsedMatchSeconds } from '../utils/matchClock';
import './SharedMatch.css';

function SharedMatch({ shareId, match: directMatch, nightMode, showBackButton = false, onBack, isAuthenticated = false, onEdit }) {
  const [match, setMatch] = useState(directMatch || null);
  const [loading, setLoading] = useState(!directMatch);
  const [error, setError] = useState('');
  const [showScoring, setShowScoring] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const liveRef = useRef(!!directMatch?.isLive);
  liveRef.current = !!match?.isLive;

  useEffect(() => {
    if (!shareId || directMatch) {
      if (directMatch) {
        setMatch(directMatch);
        setLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    const fetchSharedMatch = async (initial) => {
      try {
        if (initial) setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/matches/share/${shareId}`);
        if (!cancelled) {
          setMatch(response.data);
          setError('');
        }
      } catch (fetchError) {
        console.error('Error fetching shared match:', fetchError);
        if (!cancelled && initial) {
          setError('Match not found or no longer available for sharing.');
        }
      } finally {
        if (!cancelled && initial) setLoading(false);
      }
    };

    fetchSharedMatch(true);
    const intervalId = setInterval(() => {
      if (liveRef.current) fetchSharedMatch(false);
    }, 2000);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && liveRef.current) fetchSharedMatch(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shareId, directMatch]);

  useEffect(() => {
    if (!match?.isLive || match.clockStartedAt == null) return undefined;
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [match?.isLive, match?.clockStartedAt]);

  const formatTime = (seconds) => {
    const safe = Math.max(0, Math.floor(seconds || 0));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const remainder = safe % 60;
    const clock = `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
    return hours > 0 ? `${hours}:${clock}` : clock;
  };

  const renderTimeoutIndicator = (timeouts) => {
    return (
      <div className="timeout-indicators">
        {timeouts.map((used, index) => (
          <div key={index} className={`timeout-dot ${used ? 'used' : 'available'}`}></div>
        ))}
      </div>
    );
  };

  const renderScoringOrder = (scoringOrder, match) => {
    if (!scoringOrder || scoringOrder.length === 0) {
      return 'No scoring recorded';
    }
    
    const isTeamAWinner = match.scoreA > match.scoreB;
    const runs = findScoringRuns(scoringOrder);
    
    // Function to check if index is in a run
    const isInRun = (index) => {
      return runs.find(run => index >= run.start && index <= run.end);
    };
    
    const elements = [];
    let i = 0;
    
    while (i < scoringOrder.length) {
      const run = isInRun(i);
      
      if (run) {
        // Create a group for the run
        const runElements = [];
        for (let j = run.start; j <= run.end; j++) {
          const score = scoringOrder[j];
          runElements.push(
            <span 
              key={j} 
              className={scoringBoxClassName(score, isTeamAWinner)}
              title={isAce(score) ? 'Ace serve' : undefined}
            >
              {scoringBoxLabel(score)}
            </span>
          );
        }
        
        elements.push(
          <span key={`run-${run.start}`} className={`scoring-run run-${run.team.toLowerCase()}`}>
            {runElements}
          </span>
        );
        
        i = run.end + 1;
      } else {
        // Single point not in a run
        const score = scoringOrder[i];
        elements.push(
          <span 
            key={i} 
            className={scoringBoxClassName(score, isTeamAWinner)}
            title={isAce(score) ? 'Ace serve' : undefined}
          >
            {scoringBoxLabel(score)}
          </span>
        );
        i++;
      }
    }
    
    return elements;
  };

  const generateChartData = (match) => {
    let scoreDataA = 0;
    let scoreDataB = 0;
    return match.scoringOrder.map((team, index) => {
      if (isTeamAPoint(team)) scoreDataA++;
      if (isTeamBPoint(team)) scoreDataB++;
      // Timeouts (TA1, TA2, TB1, TB2) don't increment scores
      const leadingTeam = scoreDataA > scoreDataB ? match.teamA : (scoreDataB > scoreDataA ? match.teamB : 'Tie');
      return {
        totalPoints: index + 1,
        [match.teamA]: scoreDataA,
        [match.teamB]: scoreDataB,
        pointDifferential: Math.abs(scoreDataA - scoreDataB),
        leadingTeam: leadingTeam,
        barColor: leadingTeam === match.teamA ? "#28a745" : (leadingTeam === match.teamB ? "#dc3545" : "#6c757d")
      };
    });
  };

  const renderScoreProgressionChart = (match) => {
    if (!match.scoringOrder || match.scoringOrder.length === 0) {
      return null;
    }

    const chartData = generateChartData(match);

    return (
      <div className="score-chart">
        <h4>Score Progression</h4>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 15, left: 10, bottom: 30 }}>
            <XAxis 
              dataKey="totalPoints" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Points', position: 'insideBottom', offset: -5, style: { fontSize: '10px' } }} 
            />
            <YAxis 
              yAxisId="left" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }} 
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              tick={{ fontSize: 10 }}
              label={{ value: 'Diff', angle: 90, position: 'insideRight', style: { fontSize: '10px' } }} 
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="custom-tooltip">
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px' }}>{`Point: ${label}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', color: '#28a745' }}>{`${match.teamA}: ${data[match.teamA]}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', color: '#dc3545' }}>{`${match.teamB}: ${data[match.teamB]}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px' }}>{`Diff: ${data.pointDifferential}`}</p>
                      <p style={{ margin: 0, padding: '2px 0', fontSize: '12px', fontWeight: 'bold' }}>{`Leading: ${data.leadingTeam}`}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
              iconSize={10}
            />
            <Bar 
              yAxisId="right" 
              dataKey="pointDifferential" 
              opacity={0.8} 
              name="Point Differential"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.barColor} />
              ))}
            </Bar>
            <Line yAxisId="left" type="monotone" dataKey={match.teamA} stroke="#28a745" strokeWidth={2} name={match.teamA} />
            <Line yAxisId="left" type="monotone" dataKey={match.teamB} stroke="#dc3545" strokeWidth={2} name={match.teamB} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`shared-match-container ${nightMode ? 'night-mode' : ''}`}>
        <div className="loading-spinner">Loading shared match...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shared-match-container ${nightMode ? 'night-mode' : ''}`}>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const rotationCount = match.scoringOrder?.length
    ? getServeRotationStats(match.scoringOrder).rotations
    : null;
  const shownTime = match.isLive
    ? elapsedMatchSeconds({
      baseSeconds: Number.isFinite(match.clockBaseSeconds) ? match.clockBaseSeconds : match.time,
      startedAt: match.clockStartedAt
    }, now)
    : match.time;

  const whenPlayed = `${new Date(match.date).toLocaleDateString()} · ${new Date(match.date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;

  return (
    <div className={`shared-match-container ${nightMode ? 'night-mode' : ''}`}>
      <div className="shared-match-header">
        <div className="header-actions">
          {showBackButton && (
            <button onClick={onBack} className="back-btn">
              <ArrowLeft size={16} /> Back
            </button>
          )}
          {isAuthenticated && onEdit && (
            <button
              onClick={() => onEdit(match)}
              className="edit-btn"
            >
              <Edit size={14} /> Edit
            </button>
          )}
          {match.isLive && <span className="live-badge">Live</span>}
          <div className="match-date">
            {match.isLive ? 'Updating as points are scored' : whenPlayed}
          </div>
        </div>
        {match.user && !isAuthenticated && (
          <div className="shared-by">
            Shared by <strong>{match.user.name || match.user.username}</strong>
          </div>
        )}
      </div>

      <section className="court" aria-label={`${match.teamA} ${match.scoreA}, ${match.teamB} ${match.scoreB}`}>
        <div className="court-team team-a">
          <div className="court-name">{match.teamA}</div>
          <div className={`court-score ${match.scoreA > match.scoreB ? 'leading' : ''}`}>{match.scoreA}</div>
          {match.timeoutsA && renderTimeoutIndicator(match.timeoutsA)}
        </div>
        <div className="court-mid">
          <div className="court-clock">{formatTime(shownTime)}</div>
          <div className="court-vs">VS</div>
        </div>
        <div className="court-team team-b">
          <div className="court-name">{match.teamB}</div>
          <div className={`court-score ${match.scoreB > match.scoreA ? 'leading' : ''}`}>{match.scoreB}</div>
          {match.timeoutsB && renderTimeoutIndicator(match.timeoutsB)}
        </div>
      </section>

      <div className="match-meta-row">
        {rotationCount !== null && <span>{rotationCount} rotations</span>}
        {match.setNumber > 1 && <span>Set {match.setNumber}</span>}
        {match.matchGroup && (
          <span>
            {match.matchGroup.name}
            {match.matchGroup.type ? ` · ${match.matchGroup.type}` : ''}
          </span>
        )}
      </div>
      {match.note && <div className="match-note-line">{match.note}</div>}

      {match.scoringOrder?.length > 0 && (
        <div className="match-expanders">
          <div className="expander-row">
            <button
              type="button"
              className={`expand-toggle ${showScoring ? 'open' : ''}`}
              onClick={() => setShowScoring((open) => !open)}
            >
              Scoring order
            </button>
            <button
              type="button"
              className={`expand-toggle ${showChart ? 'open' : ''}`}
              onClick={() => setShowChart((open) => !open)}
            >
              Score progression
            </button>
          </div>
          {showScoring && (
            <div className="match-scoring-order">
              {renderScoringOrder(match.scoringOrder, match)}
            </div>
          )}
          {showChart && renderScoreProgressionChart(match)}
        </div>
      )}
    </div>
  );
}

export default SharedMatch;
